import { useContext, useEffect, useRef, useState } from 'react';
import { FormControl, TextField, Typography, Grid } from '@mui/material';
import IdRequirementsAlert from '@pages/onboarding/components/IdRequirementsAlert/IdRequirementsAlert';
import useEntityType from '@pages/onboarding/Mexico/hooks/useEntityType';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import FileInputV3 from '@efex-components/EfexDS/FileInput/FileInput';
import { documentTypeOptions } from '@pages/onboarding/Mexico/constants';
import { MexicoStakeHoldersFormData } from '@pages/onboarding/Mexico/types/stakeholders.type';
import useEffectOnce from '@main-hooks/useEffectOnce';
import StakeHolderContext from '../context/StakeHolderContext';
import GovermentIdSelector from './GovermentIdSelector';

interface DocumentTypeProps {
  defaultValues:
    | Omit<MexicoStakeHoldersFormData, 'neighborhood' | 'secondLastName'>
    | undefined;
}

const DocumentType = ({ defaultValues }: DocumentTypeProps) => {
  const [fileTypeValidation, setFileTypeValidation] = useState<
    string | undefined
  >(undefined);

  const isInitialized = useRef<boolean>(false);

  const { t } = useTranslation();

  const {
    personType,
    changeDocument,
    deleteGovernmentIdDocument,
    govermentIdDocuments,
    setGovermentIdDocuments,
    handleDropChangeDocument,
  } = useContext(StakeHolderContext);

  const { isNaturalPerson, isJuridicalMX, isJuridicalFR } = useEntityType({
    personType,
  });

  const {
    register,
    control,
    formState: { errors },
    setValue,
  } = useFormContext();

  const getDocumentTitle = (
    juridicalMX: boolean,
    juridicalFR: boolean
  ): string => {
    if (juridicalMX) return t('kybStepOneSecond');
    if (juridicalFR) return t('kybIncorporationCertificate');
    return '';
  };

  const validateTypeFile = (value: string) => {
    if (isInitialized.current && value !== fileTypeValidation) {
      setValue('idNumber', '');

      deleteGovernmentIdDocument('first');
      deleteGovernmentIdDocument('second');
    }

    setFileTypeValidation(value);
  };

  useEffectOnce(() => {
    setGovermentIdDocuments(defaultValues?.documents || []);
    if (defaultValues?.idType) {
      setFileTypeValidation(defaultValues.idType);
    }

    isInitialized.current = true;
  });

  useEffect(() => {
    const documentConfig = {
      [documentTypeOptions[1].value]: {
        frontDocument: {
          field: 'passport',
          value: govermentIdDocuments.documentOne || null,
        },
        backDocument: null,
      },
      [documentTypeOptions[0].value]: {
        frontDocument: {
          field: 'personalIdentification',
          value: govermentIdDocuments.documentOne || null,
        },
        backDocument: {
          field: 'personalIdentificationBack',
          value: govermentIdDocuments.documentTwo || null,
        },
      },
    };

    const updateFormFields = () => {
      if (!isNaturalPerson) {
        setValue('passport', govermentIdDocuments.documentOne || null, {
          shouldValidate: true,
        });
        return;
      }

      const config = documentConfig[fileTypeValidation as string];

      if (!config) return;

      if (config.frontDocument) {
        setValue(config.frontDocument.field, config.frontDocument.value, {
          shouldValidate: true,
        });
      }

      if (config.backDocument) {
        setValue(config.backDocument.field, config.backDocument.value, {
          shouldValidate: true,
        });
      }
    };

    updateFormFields();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [govermentIdDocuments, fileTypeValidation, isNaturalPerson]);

  return (
    <>
      {isNaturalPerson && (
        <Grid xs={12} className="!mt-8">
          <Typography variant="subtitle1" className="!ml-2">
            {t('onbStHlGovId')}
          </Typography>
          <IdRequirementsAlert />
        </Grid>
      )}
      {isNaturalPerson ? (
        <>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <Typography variant="subtitle2" id="idType">
                {t('onbStHlIdType')}
              </Typography>
              <Controller
                name="idType"
                rules={{ required: true }}
                control={control}
                render={({ field }) => (
                  <GovermentIdSelector
                    value={field.value}
                    onChange={(val) => {
                      field.onChange(val);
                      validateTypeFile(val);
                    }}
                    validateTypeFile={validateTypeFile}
                  />
                )}
              />
              {errors && errors.idType && (
                <p className="text-orange-600 text-xs mt-1">
                  {t('defaultError')}
                </p>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <Typography variant="subtitle2">
                {t(`onbStHlIdNumber`)}
              </Typography>
              <TextField
                autoComplete="off"
                type="text"
                className="custom-textfield "
                placeholder={t('onbStHlIdNumber') || ''}
                error={!!errors.idNumber}
                {...register('idNumber', { required: true })}
              />
              {errors && errors.idNumber && (
                <p className="text-orange-600 text-xs mt-1">
                  {t('defaultError')}
                </p>
              )}
            </FormControl>
          </Grid>
          {fileTypeValidation === documentTypeOptions[1].value && (
            <Grid item xs={12} md={6}>
              <Controller
                name="passport"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <div>
                    <FileInputV3
                      isSingleFile
                      inputName="passport"
                      onChange={(e) => {
                        changeDocument(e);
                        field.onChange(e);
                      }}
                      onDropChange={handleDropChangeDocument}
                      onDelete={() => {
                        deleteGovernmentIdDocument('first');
                        field.onChange(null);
                      }}
                      title={t('onbStHlIdFileFront') || ''}
                      documents={
                        govermentIdDocuments.documentOne
                          ? [govermentIdDocuments.documentOne]
                          : []
                      }
                    />
                    {errors.passport && (
                      <p className="text-orange-600 text-xs mt-1">
                        {t('defaultError')}
                      </p>
                    )}
                  </div>
                )}
              />
            </Grid>
          )}
          {fileTypeValidation === documentTypeOptions[0].value && (
            <>
              <Grid item xs={12} md={6}>
                <Controller
                  name="personalIdentification"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <div>
                      <FileInputV3
                        isSingleFile
                        inputName="personalIdentification"
                        onChange={(e) => {
                          changeDocument(e);
                          field.onChange(e);
                        }}
                        onDropChange={handleDropChangeDocument}
                        onDelete={() => {
                          deleteGovernmentIdDocument('first');
                          field.onChange(null);
                        }}
                        title={t('onbStHlIdFileFront') || ''}
                        documents={
                          govermentIdDocuments.documentOne
                            ? [govermentIdDocuments.documentOne]
                            : []
                        }
                      />
                      {errors.personalIdentification && (
                        <p className="text-orange-600 text-xs mt-1">
                          {t('defaultError')}
                        </p>
                      )}
                    </div>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="personalIdentificationBack"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <div>
                      <FileInputV3
                        isSingleFile
                        inputName="personalIdentificationBack"
                        onChange={(e) => {
                          changeDocument(e);
                          field.onChange(e);
                        }}
                        onDropChange={handleDropChangeDocument}
                        onDelete={() => {
                          deleteGovernmentIdDocument('second');
                          field.onChange(null);
                        }}
                        title={t('onbStHlIdFileBack') || ''}
                        documents={
                          govermentIdDocuments.documentTwo
                            ? [govermentIdDocuments.documentTwo]
                            : []
                        }
                      />
                      {errors.personalIdentificationBack && (
                        <p className="text-orange-600 text-xs mt-1">
                          {t('defaultError')}
                        </p>
                      )}
                    </div>
                  )}
                />
              </Grid>
            </>
          )}
        </>
      ) : (
        <Grid item xs={12}>
          <Controller
            name="passport"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <div>
                <FileInputV3
                  isSingleFile
                  inputName="passport"
                  onChange={(e) => {
                    changeDocument(e);
                    field.onChange(e);
                  }}
                  onDropChange={handleDropChangeDocument}
                  onDelete={() => {
                    deleteGovernmentIdDocument('first');
                    field.onChange(null);
                  }}
                  title={getDocumentTitle(isJuridicalMX, isJuridicalFR)}
                  documents={
                    govermentIdDocuments.documentOne
                      ? [govermentIdDocuments.documentOne]
                      : []
                  }
                />
                {errors.passport && (
                  <p className="text-orange-600 text-xs mt-1">
                    {t('defaultError')}
                  </p>
                )}
              </div>
            )}
          />
        </Grid>
      )}
    </>
  );
};

export default DocumentType;
